<template>
  <div class="responsive-filter-sheet">
    <slot v-if="!isMobile" name="desktop" />

    <v-card-title v-else class="responsive-filter-sheet__mobile-toolbar">
      <div v-if="showFilters" class="responsive-filter-sheet__mobile-actions">
        <v-btn color="primary" text @click="openMobileFilters">
          <v-icon left>mdi-filter-variant</v-icon>
          {{ $t('LABELS.FILTERS') }}
          <span v-if="activeFilterCount" class="responsive-filter-sheet__count">{{ activeFilterCount }}</span>
        </v-btn>
        <v-spacer v-if="hideSearchInput" />
        <v-btn
          v-if="hideSearchInput"
          icon
          color="error"
          :aria-label="$t('LABELS.CLEAR')"
          @click="$emit('clear')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <div v-if="!hideSearchInput" class="responsive-filter-sheet__search">
        <FormInput
          v-model="localSearchQuery"
          type="text"
          :i18n="{ placeholder: 'LABELS.SEARCH' }"
          :validateState="{}"
          @keyup.enter="$emit('search')"
        />
        <v-btn icon color="primary" :aria-label="$t('LABELS.SEARCH')" @click="$emit('search')">
          <v-icon>search</v-icon>
        </v-btn>
        <v-btn icon color="error" :aria-label="$t('LABELS.CLEAR')" @click="$emit('clear')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </v-card-title>

    <v-dialog
      v-if="showFilters"
      v-model="isMobileFilterDialogOpen"
      fullscreen
      scrollable
      transition="dialog-bottom-transition"
    >
      <v-card class="responsive-filter-sheet__dialog-card">
        <v-toolbar flat>
          <v-btn icon :aria-label="$t('BUTTON.CLOSE')" @click="isMobileFilterDialogOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ $t('LABELS.FILTERS') }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="px-4 pt-4 pb-0">
          <div class="responsive-filter-sheet__dialog-fields">
            <slot
              name="mobile-fields"
              :filters="draftFilters"
              :update-filter="updateDraftFilter"
            />
          </div>
        </v-card-text>
        <v-card-actions class="responsive-filter-sheet__dialog-actions">
          <button
            type="button"
            class="btn btn-light-danger font-weight-bolder"
            @click="clearMobileFilters"
          >
            {{ $t('LABELS.CLEAR') }}
          </button>
          <v-spacer />
          <button
            type="button"
            class="btn btn-dark font-weight-bold"
            @click="applyMobileFilters"
          >
            {{ $t('BUTTON.APPLY') }}
          </button>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FormInput from "@/view/content/forms/components/FormInput.vue";

export default {
  name: "ResponsiveFilterSheet",
  components: {
    FormInput
  },
  props: {
    filters: {
      type: Object,
      required: true
    },
    searchQuery: {
      type: String,
      default: ""
    },
    mobileBreakpoint: {
      type: Number,
      default: 768
    },
    mobileSheet: {
      type: Boolean,
      default: true
    },
    hideSearchInput: {
      type: Boolean,
      default: false
    },
    showFilters: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isMobileFilterDialogOpen: false,
      draftFilters: {}
    };
  },
  computed: {
    isMobile() {
      return this.mobileSheet && this.$vuetify.breakpoint.width < this.mobileBreakpoint;
    },
    activeFilterCount() {
      return Object.values(this.filters)
        .filter(value => Array.isArray(value)
          ? value.length
          : value !== null && value !== undefined && value !== "")
        .length;
    },
    localSearchQuery: {
      get() {
        return this.searchQuery;
      },
      set(value) {
        this.$emit("update:searchQuery", value);
      }
    },
    localFilters: {
      get() {
        return this.filters;
      },
      set(value) {
        this.$emit("update:filters", value);
      }
    }
  },
  watch: {
    isMobile(isMobile) {
      if (!isMobile) {
        this.isMobileFilterDialogOpen = false;
      }
    }
  },
  methods: {
    openMobileFilters() {
      this.draftFilters = JSON.parse(JSON.stringify(this.localFilters));
      this.isMobileFilterDialogOpen = true;
    },
    updateDraftFilter(key, value) {
      this.draftFilters = {
        ...this.draftFilters,
        [key]: Array.isArray(value)
          ? value
          : value === undefined || value === "" ? null : value
      };
    },
    applyMobileFilters() {
      this.localFilters = this.draftFilters;
      this.isMobileFilterDialogOpen = false;
      this.$emit("search");
    },
    clearMobileFilters() {
      this.draftFilters = Object.keys(this.draftFilters).reduce((filters, key) => {
        filters[key] = Array.isArray(this.draftFilters[key]) ? [] : null;

        return filters;
      }, {});
    }
  }
};
</script>

<style scoped>
.responsive-filter-sheet__mobile-toolbar {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.responsive-filter-sheet__search {
  align-items: center;
  display: flex;
  flex: 1 1 100%;
  min-width: 0;
}

.responsive-filter-sheet__search > *:first-child {
  flex: 1 1 auto;
  min-width: 0;
}

.responsive-filter-sheet__mobile-actions {
  align-items: center;
  display: flex;
  flex: 1 1 100%;
}

.responsive-filter-sheet__count {
  align-items: center;
  background: #3699ff;
  border-radius: 999px;
  color: #fff;
  display: inline-flex;
  font-size: 0.75rem;
  height: 1.25rem;
  justify-content: center;
  margin-left: 0.35rem;
  min-width: 1.25rem;
}

.responsive-filter-sheet__dialog-actions {
  border-top: 1px solid #e4e6ef;
  margin-top: auto;
  padding: 0.5rem 1rem;
}

.responsive-filter-sheet__dialog-card {
  min-height: 100%;
}

.responsive-filter-sheet__dialog-card > .v-toolbar {
  border-bottom: 1px solid #e4e6ef;
  flex: 0 0 56px;
}

.responsive-filter-sheet__dialog-card > .v-card__text {
  display: block;
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
}

.responsive-filter-sheet__dialog-card > .responsive-filter-sheet__dialog-actions {
  flex: 0 0 auto;
}

.responsive-filter-sheet__dialog-fields {
  display: block;
}
</style>
